import { Component, ElementRef, Input, OnChanges, OnDestroy, SimpleChanges, ViewChild, afterNextRender, inject } from '@angular/core';
import { ScrollTrigger, gsap } from '../gsap';
import { QUERIES, hasFinePointer } from '../motion-env';
import { MotionService } from '../motion.service';
import { canRun3D, force3D, pickTier } from './capability';
// Type-only: three.js is reached exclusively through the lazy `import('./night-drive.scene')`.
import type { NightDriveBailReason, NightDriveScene } from './night-drive.scene';

type SceneModule = typeof import('./night-drive.scene');

const FADE_OUT_MS = 420;

/**
 * Decorative night-highway backdrop that fills its positioned parent. A poster (or the
 * gradient-only fallback) is always underneath; the WebGL scene is lazy-loaded near the
 * viewport, only with motion allowed, a capable device and WebGL2, and runs only while visible.
 */
@Component({
  selector: 'app-night-drive',
  template: `
    <div class="nd-sky"></div>
    <ng-container *ngIf="poster">
      <img class="nd-poster" [src]="poster" alt="" loading="lazy" decoding="async" />
      <div class="nd-tint"></div>
    </ng-container>
    <div class="nd-lanes" *ngIf="!poster"><i></i><i></i><i></i></div>
    <canvas #canvas class="nd-canvas" aria-hidden="true"></canvas>
  `,
  host: {
    'aria-hidden': 'true',
    '[class.nd--band]': "mode === 'band'",
    '[class.nd--full]': "mode === 'full'",
  },
})
export class NightDriveComponent implements OnChanges, OnDestroy {
  @Input() mode: 'band' | 'full' = 'band';
  @Input() preset: 'cruise' | 'wrong-turn' = 'cruise';
  /** '' => gradient-only poster. */
  @Input() poster = '';
  @Input() boostTarget: HTMLElement | null | undefined = null;

  @ViewChild('canvas', { static: true }) private canvasRef!: ElementRef<HTMLCanvasElement>;

  private readonly host: HTMLElement = inject(ElementRef).nativeElement;
  private readonly motion = inject(MotionService);
  private mm?: ReturnType<typeof gsap.matchMedia>;
  private mod?: Promise<SceneModule>;
  private scene: NightDriveScene | null = null;
  /** A scene whose canvas is fading out after a fallback, released when the fade ends. */
  private fading: NightDriveScene | null = null;
  private canvasUsed = false;
  private runId = 0;
  private destroyed = false;
  private offscreen?: IntersectionObserver;
  private bailed = false;

  private visible = false;
  private hovering = false;
  private held = false;
  private pulseTimer: ReturnType<typeof setTimeout> | null = null;
  private unbindBoostTarget: (() => void) | null = null;
  private progressST: ScrollTrigger | null = null;

  constructor() {
    this.host.classList.add('dv-night-drive');
    // The poster is already showing; nothing here has to happen before the first paint.
    afterNextRender(() => this.motion.afterPaint(() => this.setup()));
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['boostTarget'] && this.unbindBoostTarget) this.motion.outside(() => this.bindBoostTarget());
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.offscreen?.disconnect();
    this.mm?.revert();
    this.mm = undefined;
    if (this.scene) this.release(this.scene);
    if (this.fading) this.release(this.fading);
    this.scene = this.fading = null;
    this.unbindBoostTarget?.();
    this.unbindBoostTarget = null;
    if (this.pulseTimer) clearTimeout(this.pulseTimer);
  }

  /** Keeps the boost on (e.g. during the scroll to #contact after a CTA click). */
  holdBoost(on: boolean): void {
    this.held = on;
    this.applyBoost();
  }

  /** Short boost for touch, where there is no hover. */
  pulseBoost(ms = 900): void {
    this.motion.outside(() => {
      if (this.pulseTimer) clearTimeout(this.pulseTimer);
      this.pulseTimer = setTimeout(() => {
        this.pulseTimer = null;
        this.applyBoost();
      }, ms);
      this.applyBoost();
    });
  }

  private setup(): void {
    if (this.destroyed) return;
    // Lets CSS pause the poster pan while it can't be seen (the WebGL loop pauses on its own).
    this.offscreen = new IntersectionObserver(([entry]) => this.host.classList.toggle('nd--offscreen', !entry.isIntersecting));
    this.offscreen.observe(this.host);
    const mm = (this.mm = gsap.matchMedia(this.host));
    // Both conditions, so the callback also runs (and settles on the poster) under reduced motion.
    mm.add({ motion: QUERIES.motion, reduce: QUERIES.reduce }, (ctx) => {
      if (!ctx.conditions!['motion'] || this.bailed || !canRun3D()) {
        this.host.classList.add('nd--static');
        return;
      }
      return this.start();
    });
  }

  /** Motion branch: observers, scroll progress, pointer and boost wiring. Returns the cleanup. */
  private start(): () => void {
    const host = this.host;
    const parent = host.parentElement ?? host;
    const run = ++this.runId;
    host.classList.remove('nd--static');

    const prefetch = new IntersectionObserver(
      (entries) => {
        if (!entries.some((e) => e.isIntersecting)) return;
        prefetch.disconnect();
        const fetchScene = () => void this.load().catch(() => (this.mod = undefined));
        if ('requestIdleCallback' in window) window.requestIdleCallback(fetchScene);
        else setTimeout(fetchScene, 1);
      },
      { rootMargin: '200% 0px' }
    );
    prefetch.observe(host);

    const create = new IntersectionObserver(
      (entries) => {
        if (!entries.some((e) => e.isIntersecting)) return;
        create.disconnect();
        this.createScene(run);
      },
      { rootMargin: '50% 0px' }
    );
    create.observe(host);

    const visibility = new IntersectionObserver(
      (entries) => {
        this.visible = entries[entries.length - 1].isIntersecting;
        this.applyRunning();
      },
      { threshold: 0 }
    );
    visibility.observe(host);
    const onVisibilityChange = () => this.applyRunning();
    document.addEventListener('visibilitychange', onVisibilityChange);

    this.progressST =
      this.mode === 'band'
        ? ScrollTrigger.create({
            trigger: parent,
            start: 'top bottom',
            end: 'bottom top',
            onUpdate: (st) => this.scene?.setProgress(st.progress),
          })
        : null;

    let pointerFrame = 0;
    let pointerX = 0;
    const applyPointer = () => {
      pointerFrame = 0;
      const rect = parent.getBoundingClientRect();
      this.scene?.setPointer(rect.width ? ((pointerX - rect.left) / rect.width) * 2 - 1 : 0);
    };
    const onPointerMove = (e: PointerEvent) => {
      if (e.pointerType !== 'mouse') return;
      pointerX = e.clientX;
      if (!pointerFrame) pointerFrame = requestAnimationFrame(applyPointer);
    };
    const onPointerLeave = () => {
      if (pointerFrame) cancelAnimationFrame(pointerFrame);
      pointerFrame = 0;
      this.scene?.setPointer(0);
    };
    const finePointer = hasFinePointer();
    if (finePointer) {
      parent.addEventListener('pointermove', onPointerMove, { passive: true });
      parent.addEventListener('pointerleave', onPointerLeave);
    }

    this.bindBoostTarget();

    return () => {
      this.runId++;
      prefetch.disconnect();
      create.disconnect();
      visibility.disconnect();
      document.removeEventListener('visibilitychange', onVisibilityChange);
      if (finePointer) {
        parent.removeEventListener('pointermove', onPointerMove);
        parent.removeEventListener('pointerleave', onPointerLeave);
      }
      if (pointerFrame) cancelAnimationFrame(pointerFrame);
      this.progressST = null; // killed by the matchMedia revert
      this.unbindBoostTarget?.();
      this.unbindBoostTarget = null;
      this.visible = false;
      this.fallback();
    };
  }

  private load(): Promise<SceneModule> {
    this.mod ??= import('./night-drive.scene');
    return this.mod;
  }

  private async createScene(run: number): Promise<void> {
    let mod: SceneModule;
    try {
      mod = await this.load();
    } catch {
      this.mod = undefined; // a failed chunk fetch may succeed on a later visit
      if (run === this.runId && !this.destroyed) this.fallback();
      return;
    }
    if (run !== this.runId || this.destroyed || this.scene) return;

    const forced = force3D();
    const tier = pickTier();
    try {
      this.scene = new mod.NightDriveScene(this.freshCanvas(), {
        mode: this.mode,
        preset: this.preset,
        tier,
        seed: 2026,
        getVelocity: () => this.motion.scrollVelocity(),
        onFirstFrame: () => this.host.classList.add('nd--live'),
        onBail: (reason: NightDriveBailReason) => {
          // The forced test path (SwiftShader) is always slow; only a lost context ends it.
          if (reason === 'slow' && forced) return;
          this.bailed = true;
          this.fallback();
        },
        onTier: (next) => (this.host.dataset['ndTier'] = next),
      });
    } catch {
      this.scene = null;
      this.bailed = true;
      this.fallback();
      return;
    }
    this.motion.track('webgl', 1);
    this.host.dataset['ndTier'] = tier;
    this.scene.setProgress(this.progressST?.progress ?? 0);
    this.applyBoost();
    this.applyRunning();
  }

  /** A canvas whose context was lost cannot get a new one, so every scene after the first gets a clone. */
  private freshCanvas(): HTMLCanvasElement {
    const canvas = this.canvasRef.nativeElement;
    if (!this.canvasUsed) {
      this.canvasUsed = true;
      return canvas;
    }
    const clone = canvas.cloneNode(false) as HTMLCanvasElement;
    canvas.replaceWith(clone);
    this.canvasRef = new ElementRef(clone);
    return clone;
  }

  private applyRunning(): void {
    this.scene?.setRunning(this.visible && !document.hidden);
  }

  private applyBoost(): void {
    const scene = this.scene;
    if (!scene) return;
    const on = this.hovering || this.held || this.pulseTimer !== null;
    if (this.preset === 'wrong-turn') {
      scene.setSteerTarget(on ? 0 : 0.9);
      scene.setBase(on ? 2 : 0.6);
    } else {
      scene.setBoost(on ? 2.2 : 0);
    }
  }

  private bindBoostTarget(): void {
    this.unbindBoostTarget?.();
    const target = this.boostTarget;
    if (!target) {
      this.unbindBoostTarget = () => {};
      return;
    }
    const on = () => {
      this.hovering = true;
      this.applyBoost();
    };
    const off = () => {
      this.hovering = false;
      this.applyBoost();
    };
    // Keyboard focus only: a tapped or clicked link keeps focus after the scroll to #contact,
    // which would otherwise leave the boost on.
    const onFocus = () => {
      if (focusVisible(target)) on();
    };
    const onPointerDown = (e: PointerEvent) => {
      if (e.pointerType !== 'mouse') this.pulseBoost(900);
    };
    target.addEventListener('pointerenter', on);
    target.addEventListener('focus', onFocus);
    target.addEventListener('pointerleave', off);
    target.addEventListener('blur', off);
    target.addEventListener('pointerdown', onPointerDown, { passive: true });
    this.unbindBoostTarget = () => {
      target.removeEventListener('pointerenter', on);
      target.removeEventListener('focus', onFocus);
      target.removeEventListener('pointerleave', off);
      target.removeEventListener('blur', off);
      target.removeEventListener('pointerdown', onPointerDown);
      this.hovering = false;
    };
  }

  /** Back to the poster: the canvas fades out (CSS), then the scene and its context are released. */
  private fallback(): void {
    const host = this.host;
    host.classList.remove('nd--live');
    host.classList.add('nd--static');
    delete host.dataset['ndTier'];
    const scene = this.scene;
    if (!scene) return;
    this.scene = null;
    if (this.destroyed) {
      this.release(scene);
      return;
    }
    scene.setRunning(false);
    if (this.fading) this.release(this.fading);
    this.fading = scene;
    setTimeout(() => {
      if (this.fading !== scene) return;
      this.fading = null;
      this.release(scene);
    }, FADE_OUT_MS);
  }

  private release(scene: NightDriveScene): void {
    scene.dispose();
    this.motion.track('webgl', -1);
  }
}

function focusVisible(el: HTMLElement): boolean {
  try {
    return el.matches(':focus-visible');
  } catch {
    return true; // no :focus-visible support: treat every focus as keyboard focus
  }
}
